@import '../../themes/capsule.globals';

:host {
  --container-box-shadow: none;
  --container-box-shadow-color: none;
  --container-border-color: var(--cpsl-color-input-border-active);
  --container-border-error-color: var(--cpsl-color-input-border-error);
  --container-background-color: var(--cpsl-color-input-surface-default);
  --container-background-color-disabled: var(--cpsl-color-input-surface-disabled);
  --container-border-width: 1px;
  --container-border-radius: var(--cpsl-border-radius-input);
  --container-padding-top: 0px;
  --container-padding-bottom: 0px;
  --container-padding-end: 12px;
  --container-padding-start: 12px;
  --container-height: 48px;
  --container-gap: 8px;

  --input-background-color: var(--cpsl-color-input-surface-default);
  --input-background-color-disabled: var(--cpsl-color-input-surface-disabled);
  --input-color: var(--cpsl-color-text-primary);
  --input-placeholder-color: var(--cpsl-color-text-secondary);
  --input-font-size: var(--cpsl-font-size-body-m);
  --input-font-weight: 400;

  --optional-label-color: var(--cpsl-color-text-secondary);
  --label-color: var(--cpsl-color-text-primary);
  --label-font-size: var(--cpsl-font-size-body-s);
  --label-margin-bottom: 0px;

  --helper-text-color: var(--cpsl-color-text-secondary);
  --helper-text-error-color: var(--cpsl-color-text-error);
  --helper-text-icon-color: var(--cpsl-color-text-secondary);
  --helper-text-icon-error-color: var(--cpsl-color-text-error);
  --helper-text-icon-size: 16px;
  --helper-text-font-size: var(--cpsl-font-size-body-xs);
  --helper-text-margin-top: 0px;

  --button-padding: 8px;

  display: flex;
  flex-direction: column;
  gap: 4px;

  font-family: $font-family-base;
}

:host(.contrast-text) {
  --input-color: var(--cpsl-color-contrast);
}

:host(:not(.has-value)) {
  --container-border-color: var(--cpsl-color-input-border-placeholder);
  --input-color: var(--cpsl-color-text-secondary);
}

:host(.disabled) {
  --container-background-color: var(--container-background-color-disabled);
  --input-background-color: var(--input-background-color-disabled);
  --input-color: var(--cpsl-color-text-disabled);
  --container-border-color: transparent;
}

:host(:not(.disabled):not(.focused):hover) {
  --container-background-color: var(--cpsl-color-input-surface-hover);
}

:host(.focused) {
  --container-box-shadow: 0px 0px 0px 2px var(--container-box-shadow-color);
  --container-border-color: var(--cpsl-color-input-border-active);
  --container-background-color: var(--cpsl-color-input-surface-default);
}

.input-container {
  @include padding(var(--container-padding-top), var(--container-padding-end), var(--container-padding-bottom), var(--container-padding-start));

  display: flex;
  align-items: center;
  box-sizing: border-box;
  height: var(--container-height);
  gap: var(--container-gap);

  border-style: var(--container-border-style, solid);
  border-width: var(--container-border-width);
  border-color: var(--container-border-color);
  border-radius: var(--container-border-radius);
  background: var(--container-background-color);

  box-shadow: var(--container-box-shadow);

  &.textarea {
    height: auto;
    min-height: var(--container-height);
  }
}

.error-container {
  border-color: var(--container-border-error-color);
}

.native-input {
  flex: 1;
  min-width: 0;
  border: none;
  height: 100%;
  box-sizing: border-box;

  font-size: var(--input-font-size);
  font-weight: var(--input-font-weight);
  color: var(--input-color);
  background: var(--input-background-color);
  width: var(--input-width, auto);
  text-align: var(--input-text-align, left);

  resize: none;

  &:focus,
  &:focus-visible {
    outline: none;
  }

  &::placeholder {
    color: var(--input-placeholder-color);
    opacity: 1; /* Firefox */
  }

  &::-moz-selection {
    /* Code for Firefox */
    color: var(--cpsl-color-text-inverted);
    background: var(--cpsl-color-background-64);
  }

  &::selection {
    color: var(--cpsl-color-text-inverted);
    background: var(--cpsl-color-background-64);
  }

  font-family: $font-family-base;
}

.label {
  display: inline-block;
  color: var(--label-color);
  font-size: var(--label-font-size);
  font-weight: 500;

  margin-bottom: var(--label-margin-bottom);
}

.optional-label {
  display: inline-block;
  color: var(--optional-label-color);
  // TODO: this will use a typography size once typography changes
  font-size: var(--label-font-size);
  font-weight: 500;
}

.helper-text-container {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--helper-text-color);
  font-size: var(--helper-text-font-size);
  font-weight: 500;

  margin-top: var(--helper-text-margin-top);

  cpsl-icon {
    --height: var(--helper-text-icon-size);
    --width: var(--helper-text-icon-size);
    --icon-color: var(--helper-text-icon-color);
  }
}

.error-text {
  color: var(--helper-text-error-color);

  cpsl-icon {
    --icon-color: var(--helper-text-icon-error-color);
  }
}

:host {
  ::slotted([slot='start']) {
    flex: 0 0 auto;
  }
  ::slotted([slot='end']) {
    flex: 0 0 auto;
  }
  ::slotted(cpsl-icon) {
    --icon-color: var(--input-color);
  }
  ::slotted(cpsl-button) {
    width: calc(var(--container-height) - var(--button-padding) * 2);
    height: calc(var(--container-height) - var(--button-padding) * 2);
    --button-border-radius: calc(var(--container-border-radius) - 4px);
    --button-padding-top: 4px;
    --button-padding-start: 4px;
    --button-padding-end: 4px;
    --button-padding-bottom: 4px;
  }
  slot[name='end']::slotted(cpsl-button) {
    margin-right: calc(calc(var(--container-padding-end) - var(--button-padding)) * -1);
  }
  slot[name='start']::slotted(cpsl-button) {
    margin-left: calc(calc(var(--container-padding-end) - var(--button-padding)) * -1);
  }
}
